import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	LinearProgress,
	Link,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { useSnackbar } from 'notistack';
import {
	dismissOnboarding,
	fetchOnboarding,
	sendOnboardingSampleBid,
	skipStripeOnboarding,
} from '../Shared/Api';
import { ONBOARDING_REFRESH_EVENT } from '../Shared/onboarding';
import useConfirm from './useConfirm';

const HIDDEN_STEP_IDS = new Set(['account']);

const OnboardingChecklist = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const { confirm, confirmDialog } = useConfirm();
	const [state, setState] = useState(null);
	const [loading, setLoading] = useState(true);
	const [expanded, setExpanded] = useState(false);
	const [actionLoading, setActionLoading] = useState(null);
	const hasAutoExpanded = useRef(false);

	const loadOnboarding = useCallback(async () => {
		try {
			const response = await fetchOnboarding();
			setState(response);
		} catch (error) {
			console.error('Failed to load onboarding checklist:', error);
			setState(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadOnboarding();
	}, [loadOnboarding, location.pathname, location.search]);

	useEffect(() => {
		const handleRefresh = () => {
			loadOnboarding();
		};
		window.addEventListener('focus', handleRefresh);
		window.addEventListener(ONBOARDING_REFRESH_EVENT, handleRefresh);
		return () => {
			window.removeEventListener('focus', handleRefresh);
			window.removeEventListener(ONBOARDING_REFRESH_EVENT, handleRefresh);
		};
	}, [loadOnboarding]);

	const visibleSteps = useMemo(
		() => (state?.steps ?? []).filter((step) => !HIDDEN_STEP_IDS.has(step.id)),
		[state?.steps],
	);

	const visibleProgress = useMemo(() => {
		const completed = visibleSteps.filter((step) => step.complete).length;
		return { completed, total: visibleSteps.length };
	}, [visibleSteps]);

	useEffect(() => {
		if (!state?.visible || hasAutoExpanded.current) {
			return;
		}
		const onJobsHome = location.pathname === '/jobs';
		const earlySetup = visibleProgress.completed === 0;
		if (onJobsHome && earlySetup) {
			setExpanded(true);
			hasAutoExpanded.current = true;
		}
	}, [state?.visible, visibleProgress.completed, location.pathname]);

	const incompleteSteps = useMemo(
		() => visibleSteps.filter((step) => !step.complete),
		[visibleSteps],
	);

	const nextStepId = incompleteSteps[0]?.id;

	const handleDismiss = async () => {
		const confirmed = await confirm(
			'Hide the setup guide? You can bring it back anytime from Settings.',
			{
				title: 'Hide setup guide',
				confirmText: 'Hide',
			},
		);
		if (!confirmed) {
			return;
		}

		setActionLoading('dismiss');
		try {
			const response = await dismissOnboarding();
			setState(response);
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not hide checklist.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleSkipStripe = async () => {
		setActionLoading('skip-stripe');
		try {
			const response = await skipStripeOnboarding();
			setState(response);
			enqueueSnackbar('You can connect Stripe anytime in Settings.', {
				variant: 'info',
			});
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not skip Stripe step.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleSendSampleBid = async () => {
		setActionLoading('sample-bid');
		try {
			const response = await sendOnboardingSampleBid();
			enqueueSnackbar(response?.detail || 'Sample bid sent to your email.', {
				variant: 'success',
			});
			await loadOnboarding();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to send sample bid.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleStepAction = (stepId) => {
		setExpanded(false);
		switch (stepId) {
			case 'mailing_address':
				navigate('/settings?onboarding=mailing');
				break;
			case 'logo':
				navigate('/settings?onboarding=branding');
				break;
			case 'customer':
				navigate('/customers?onboarding=1');
				break;
			case 'first_bid':
				navigate('/jobs?newJob=1');
				break;
			case 'stripe':
				navigate('/settings?stripe=1');
				break;
			case 'job_statuses':
				navigate('/settings?onboarding=job-statuses');
				break;
			default:
				break;
		}
	};

	const getStepAction = (step) => {
		switch (step.id) {
			case 'mailing_address':
				return {
					label: 'Add address',
					action: () => handleStepAction('mailing_address'),
				};
			case 'logo':
				return {
					label: 'Upload logo',
					action: () => handleStepAction('logo'),
				};
			case 'customer':
				return {
					label: 'Add customer',
					action: () => handleStepAction('customer'),
				};
			case 'stripe':
				return {
					label: 'Connect Stripe',
					action: () => handleStepAction('stripe'),
				};
			case 'job_statuses':
				return {
					label: 'Customize statuses',
					action: () => handleStepAction('job_statuses'),
				};
			default:
				return null;
		}
	};

	if (loading || !state?.visible) {
		return null;
	}

	const progress =
		visibleProgress.total > 0
			? (visibleProgress.completed / visibleProgress.total) * 100
			: 0;

	const widgetSx = {
		maxWidth: isMobile ? (expanded ? 'calc(100vw - 32px)' : 'auto') : 340,
		width: isMobile ? (expanded ? 'calc(100vw - 32px)' : 'auto') : 340,
	};

	const progressRing = (
		<Box sx={{ position: 'relative', display: 'inline-flex' }}>
			<CircularProgress
				variant="determinate"
				value={progress}
				size={isMobile && !expanded ? 48 : 36}
				thickness={4}
			/>
			<Box
				sx={{
					top: 0,
					left: 0,
					bottom: 0,
					right: 0,
					position: 'absolute',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography
					variant="caption"
					sx={{ fontWeight: 700, fontSize: isMobile && !expanded ? 11 : 10 }}
				>
					{visibleProgress.completed}/{visibleProgress.total}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<>
			{confirmDialog}
			<Box sx={widgetSx}>
				<Collapse in={!expanded}>
					{/* Compact FAB on mobile so it doesn't cover bottom actions / modals */}
					{isMobile ? (
						<Paper
							elevation={6}
							onClick={() => setExpanded(true)}
							aria-label="Open setup guide"
							sx={{
								width: 56,
								height: 56,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: 'pointer',
								border: (t) => `1px solid ${t.palette.divider}`,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							{progressRing}
						</Paper>
					) : (
						<Paper
							elevation={6}
							onClick={() => setExpanded(true)}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								px: 2,
								py: 1.25,
								borderRadius: 999,
								cursor: 'pointer',
								border: (t) => `1px solid ${t.palette.divider}`,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							{progressRing}
							<Box sx={{ minWidth: 0, flex: 1 }}>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, lineHeight: 1.2 }}
								>
									Setup guide
								</Typography>
								<Typography variant="caption" color="text.secondary" noWrap>
									{incompleteSteps[0]?.title ?? 'Continue setup'}
								</Typography>
							</Box>
							<RocketLaunchOutlinedIcon color="primary" fontSize="small" />
						</Paper>
					)}
				</Collapse>

				<Collapse in={expanded}>
					<Paper
						elevation={8}
						sx={{
							borderRadius: 2,
							border: (t) => `1px solid ${t.palette.divider}`,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							maxHeight: 'min(60vh, 420px)',
						}}
					>
						<Stack
							direction="row"
							alignItems="flex-start"
							justifyContent="space-between"
							spacing={1}
							sx={{
								px: 2,
								pt: 2,
								pb: 1.5,
								borderBottom: (t) => `1px solid ${t.palette.divider}`,
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
									Setup guide
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{visibleProgress.completed} of {visibleProgress.total}{' '}
									complete
								</Typography>
							</Box>
							<Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
								<IconButton
									size="small"
									onClick={() => setExpanded(false)}
									aria-label="Minimize checklist"
								>
									<ExpandMoreIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={handleDismiss}
									disabled={actionLoading === 'dismiss'}
									aria-label="Hide setup guide"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
						</Stack>

						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{ height: 3 }}
						/>

						<Box sx={{ overflowY: 'auto', px: 2, py: 2 }}>
							<Stack spacing={1.75}>
								{incompleteSteps.map((step) => {
									const stepAction = getStepAction(step);
									const isNext = step.id === nextStepId;
									return (
										<Box
											key={step.id}
											sx={{
												borderRadius: 1.5,
												px: 1.25,
												py: 1.25,
												bgcolor: isNext ? 'action.hover' : 'transparent',
												border: (t) =>
													isNext
														? `1px solid ${t.palette.primary.main}33`
														: '1px solid transparent',
											}}
										>
											<Stack
												direction="row"
												spacing={1.25}
												alignItems="flex-start"
											>
												<RadioButtonUncheckedIcon
													color={isNext ? 'primary' : 'disabled'}
													sx={{ mt: 0.15, fontSize: 20 }}
												/>
												<Box sx={{ minWidth: 0, flex: 1 }}>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center"
														flexWrap="wrap"
													>
														<Typography
															variant="body2"
															sx={{ fontWeight: 600 }}
														>
															{step.title}
														</Typography>
														{(step.id === 'logo' ||
															step.id === 'stripe' ||
															step.id === 'job_statuses') && (
															<Chip
																label="Optional"
																size="small"
																variant="outlined"
																sx={{ height: 20, fontSize: 11 }}
															/>
														)}
													</Stack>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
													>
														{step.description}
													</Typography>

													{step.id === 'first_bid' && (
														<Stack
															direction="row"
															spacing={1}
															sx={{ mt: 1.25 }}
															flexWrap="wrap"
															useFlexGap
														>
															<Button
																size="small"
																variant="contained"
																onClick={() => handleStepAction('first_bid')}
																sx={{ textTransform: 'none' }}
															>
																New job
															</Button>
															<Button
																size="small"
																variant="outlined"
																onClick={handleSendSampleBid}
																disabled={actionLoading === 'sample-bid'}
																sx={{ textTransform: 'none' }}
															>
																{actionLoading === 'sample-bid'
																	? 'Sending...'
																	: 'Email me a sample'}
															</Button>
														</Stack>
													)}

													{stepAction && step.id !== 'first_bid' && (
														<Button
															size="small"
															variant={
																step.id === 'stripe' ? 'outlined' : 'contained'
															}
															onClick={stepAction.action}
															sx={{ mt: 1.25, textTransform: 'none' }}
														>
															{stepAction.label}
														</Button>
													)}

													{step.id === 'stripe' && (
														<Link
															component="button"
															type="button"
															variant="caption"
															onClick={handleSkipStripe}
															disabled={actionLoading === 'skip-stripe'}
															sx={{
																display: 'block',
																mt: 1,
																textAlign: 'left',
															}}
														>
															Skip for now
														</Link>
													)}
												</Box>
											</Stack>
										</Box>
									);
								})}
							</Stack>
						</Box>
					</Paper>
				</Collapse>
			</Box>
		</>
	);
};

export default OnboardingChecklist;
